import React, { useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, IconButton, Stack, Chip } from '@mui/material';
import { Link as LinkIcon, VideoLibrary, Description, Image as ImageIcon, Close, OpenInNew } from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';

const detectLinkType = (url) => {
    if (!url) return null;

    const videoPatterns = [/youtube\.com\/watch/i, /youtu\.be\//i, /vimeo\.com/i, /dailymotion\.com/i, /\.mp4$/i, /\.webm$/i, /\.ogg$/i];

    const documentPatterns = [
        /\.pdf$/i,
        /\.doc$/i,
        /\.docx$/i,
        /\.xls$/i,
        /\.xlsx$/i,
        /\.ppt$/i,
        /\.pptx$/i,
        /docs\.google\.com\/document/i,
        /docs\.google\.com\/presentation/i,
        /drive\.google\.com\/file/i
    ];

    const imagePatterns = [/\.jpg$/i, /\.jpeg$/i, /\.png$/i, /\.gif$/i, /\.webp$/i, /\.svg$/i];

    if (videoPatterns.some((pattern) => pattern.test(url))) return 'video';
    if (documentPatterns.some((pattern) => pattern.test(url))) return 'document';
    if (imagePatterns.some((pattern) => pattern.test(url))) return 'image';
    return 'link';
};

const getEmbedUrl = (url) => {
    if (!url) return null;

    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
        return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }

    return url;
};

const buildPreview = (url, type) => {
    if (!url || !type) return null;

    switch (type) {
        case 'video': {
            const embedUrl = getEmbedUrl(url);
            return (
                <Box sx={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
                    <iframe
                        title="Vista previa de video"
                        src={embedUrl}
                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: '8px' }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </Box>
            );
        }
        case 'document': {
            if (url.toLowerCase().endsWith('.pdf')) {
                return (
                    <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            title="Vista previa de documento PDF"
                            src={url}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: '8px' }}
                        />
                    </Box>
                );
            }
            if (url.includes('docs.google.com/document') && url.includes('/edit')) {
                const embedUrl = url.replace('/edit', '/preview');
                return (
                    <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                        <iframe
                            title="Vista previa de documento"
                            src={embedUrl}
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0, borderRadius: '8px' }}
                        />
                    </Box>
                );
            }
            if (url.includes('drive.google.com/file')) {
                const fileIdMatch = url.match(/\/file\/d\/([^/]+)/);
                if (fileIdMatch) {
                    const previewUrl = `https://drive.google.com/file/d/${fileIdMatch[1]}/preview`;
                    return (
                        <Box sx={{ position: 'relative', paddingBottom: '75%', height: 0, overflow: 'hidden' }}>
                            <iframe
                                title="Vista previa de documento"
                                src={previewUrl}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    border: 0,
                                    borderRadius: '8px'
                                }}
                            />
                        </Box>
                    );
                }
            }
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                    <Description color="primary" />
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Typography variant="body2" noWrap>
                            {url}
                        </Typography>
                    </Box>
                    <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                        <OpenInNew fontSize="small" />
                    </IconButton>
                </Box>
            );
        }
        case 'image':
            return (
                <Box
                    component="img"
                    src={url}
                    alt="Preview"
                    sx={{ width: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: '8px' }}
                />
            );
        case 'link':
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: '8px' }}>
                    <LinkIcon color="primary" />
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Typography variant="body2" noWrap>
                            {url}
                        </Typography>
                    </Box>
                    <IconButton size="small" component="a" href={url} target="_blank" rel="noopener noreferrer">
                        <OpenInNew fontSize="small" />
                    </IconButton>
                </Box>
            );
        default:
            return null;
    }
};

const getLinkTypeIcon = (type) => {
    switch (type) {
        case 'video':
            return <VideoLibrary />;
        case 'document':
            return <Description />;
        case 'image':
            return <ImageIcon />;
        default:
            return <LinkIcon />;
    }
};

const getLinkTypeLabel = (type) => {
    switch (type) {
        case 'video':
            return 'Video';
        case 'document':
            return 'Documento';
        case 'image':
            return 'Imagen';
        default:
            return 'Enlace';
    }
};

// name: nombre del campo observado (por defecto 'enlace')
const EnlacePreview = ({ name = 'enlace' }) => {
    const { watch, setValue } = useFormContext();
    const url = watch(name);

    const type = useMemo(() => detectLinkType(url), [url]);
    const preview = useMemo(() => buildPreview(url, type), [url, type]);

    if (!url || !preview) return null;

    return (
        <Card sx={{ mt: 1 }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip icon={getLinkTypeIcon(type)} label={getLinkTypeLabel(type)} size="small" color="primary" variant="outlined" />
                        <Typography variant="subtitle2" color="text.secondary">
                            Vista previa
                        </Typography>
                    </Stack>
                    <IconButton size="small" onClick={() => setValue(name, '')}>
                        <Close fontSize="small" />
                    </IconButton>
                </Stack>
                {preview}
            </CardContent>
        </Card>
    );
};

export default EnlacePreview;
